import { useTranslation } from "react-i18next";
import { TableColumn } from "react-data-table-component";
import React, { ChangeEvent, useEffect, useState } from "react";
import { Container, Row, Col, Card, CardBody, Button } from "reactstrap";
import { withRouter, useHistory, RouteComponentProps } from "react-router-dom";
import { DebounceInput } from "react-debounce-input";

import { BreadCrumb } from "../../components/Common";
import { SchoolDto, PaginateQuery } from "../../interfaces";
import { Paginated } from "../../models";
import {
  ActionButton,
  AppTable,
  CitySelect,
  ColumnHeader,
  DateColumn,
  GovernorateSelect,
  TableSpinner,
  TextColumn,
} from "../../components";
import { ActionTypeEnum } from "../../enums";
import DeleteModal from "../../components/Common/DeleteModal";
import { AddEditSchoolModal } from "./AddEditSchoolModal";
import {
  useGetSchoolsQuery,
  useDeleteSchoolMutation,
} from "../../store/api/schoolApi";

type TProps = RouteComponentProps & {};
const SchoolsPageComponent: React.FC<TProps> = () => {
  //#region hooks

  const history = useHistory();
  const { t, i18n } = useTranslation();
  //#endregion

  //#region internal state
  const initState = new PaginateQuery();
  const [paginateQuery, setPaginateQuery] = useState<PaginateQuery>(initState);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [deletingItemId, setDeletingItemId] = useState<string | null>(null);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [filterGovernorateId, setFilterGovernorateId] = useState<string>("");
  const [filterCityId, setFilterCityId] = useState<string>("");

  //#endregion

  //#region RTK Query
  const { data: response, isLoading: loading } =
    useGetSchoolsQuery(paginateQuery);
  const [deleteSchool, { isLoading: deleteSchoolLoading, error: deleteSchoolError }] =
    useDeleteSchoolMutation();
  //#endregion

  document.title = `${t("documentTitle", { pageName: "schools" })}`;

  const columns: TableColumn<SchoolDto>[] = [
    {
      name: <ColumnHeader title="name" />,
      cell: (row) => (
        <TextColumn text={row.name[i18n.language === "ar" ? "ar" : "en"]} />
      ),
      sortable: true,
    },
    {
      name: <ColumnHeader title="createdAt" />,
      cell: (row) => <DateColumn date={row.createdAt} />,
      sortable: true,
    },
    {
      name: <ColumnHeader title="governorate" />,
      cell: (row) => (
        <TextColumn
          text={
            row.governorate
              ? row.governorate.name[i18n.language === "ar" ? "ar" : "en"]
              : "-"
          }
        />
      ),
      sortable: false,
    },
    {
      name: <ColumnHeader title="city" />,
      cell: (row) => (
        <TextColumn
          text={
            row.city ? row.city.name[i18n.language === "ar" ? "ar" : "en"] : "-"
          }
        />
      ),
      sortable: false,
    },
    {
      name: <ColumnHeader title="forGender" />,
      cell: (row) => <TextColumn text={row.forGender} />,
      sortable: true,
    },
    {
      name: <ColumnHeader title="action" />,
      sortable: false,
      cell: (row) => (
        <React.Fragment>
          <ActionButton
            onAction={() => onEdit(row.id)}
            actionType={ActionTypeEnum.EDIT}
          />
          &nbsp;
          <ActionButton
            onAction={() => onDelete(row.id)}
            actionType={ActionTypeEnum.DELETE}
          />
        </React.Fragment>
      ),
    },
  ];

  const getSortByColumnName = (name: string) => {
    let sortBy;
    switch (name) {
      case "name.en":
      case "name.ar":
        sortBy = 1;
        break;
      case "for_gender":
        sortBy = 5;
        break;
      default:
        sortBy = 2;
        break;
    }
    return sortBy;
  };

  const [defaultSortFieldId, setDefaultSortFieldId] = useState(
    getSortByColumnName("created_at:DESC")
  );
  const [defaultSortAsc, setDefaultSortAsc] = useState(false);
  const [defaultPage, setDefaultPage] = useState(1);

  //#region effects

  useEffect(() => {
    const queryParams = new URLSearchParams(history.location.search);
    let filters: any = {};
    if (queryParams.has("filter[governorate_id]")) {
      filters = {
        ...filters,
        ["governorate_id"]: queryParams.get("filter[governorate_id]") || "",
      };
      setFilterGovernorateId(filters["governorate_id"] as string);
    }
    if (queryParams.has("filter[city_id]")) {
      filters = {
        ...filters,
        ["city_id"]: queryParams.get("filter[city_id]") || "",
      };
      setFilterCityId(filters["city_id"] as string);
    }
    let query: PaginateQuery = {
      limit: queryParams.has("limit")
        ? Number(queryParams.get("limit"))
        : paginateQuery.limit,
      page: queryParams.has("page")
        ? Number(queryParams.get("page"))
        : paginateQuery.page,
      sortBy: queryParams.has("sortBy")
        ? (queryParams.get("sortBy") as string)
        : paginateQuery.sortBy,
      search: queryParams.has("search")
        ? (queryParams.get("search") as string)
        : paginateQuery.search,
      filter: Object.keys(filters).length > 0 ? filters : paginateQuery.filter,
    };
    if (
      queryParams.get("sortBy") &&
      queryParams.get("sortBy")?.split(":").length &&
      queryParams.get("sortBy")?.split(":").length == 2
    ) {
      setDefaultSortAsc(
        (queryParams.get("sortBy")?.split(":")[1] as string) === "ASC"
      );
      setDefaultSortFieldId(
        getSortByColumnName(queryParams.get("sortBy")?.split(":")[0] as string)
      );
    } else {
      setDefaultSortAsc(false);
      setDefaultSortFieldId(2);
    }
    if (query.filter && query.filter["governorate_id"]) {
      setFilterGovernorateId(query.filter["governorate_id"] as string);
    }
    setDefaultPage(query.page);
    setPaginateQuery(query);
  }, []);

  useEffect(() => {
    if (paginateQuery !== initState) {
      const { limit, page, search, sortBy, filter } = paginateQuery;
      let query;
      if (page) query = `?page=${page}`;
      if (limit) query += `&limit=${limit}`;
      if (search && search !== "") {
        history.replace({
          search: `&search=${search}`,
        });
      }
      if (sortBy) query += `&sortBy=${sortBy}`;
      if (search) query += `&search=${search}`;
      if (
        filter &&
        Object.keys(filter).length &&
        Object.keys(filter).length > 0
      ) {
        let filters: string = "";
        Object.keys(filter).map((key: string, index: number) => {
          if (filter[key]) filters += `&filter[${key}]=${filter[key]}`;
        });
        query += filters;
      }
      history.replace({ search: query });
    }
  }, [paginateQuery]);

  //#endregion

  //#region handlers

  const onAdd = () => {
    setShowAddEditModal(true);
  };

  const onEdit = (id: string) => {
    setEditingItemId(id);
    setShowAddEditModal(true);
  };

  const onDelete = (id: string) => {
    setDeletingItemId(id);
    setShowDeleteModal(true);
  };

  const onDeleteConfirm = async () => {
    try {
      await deleteSchool(deletingItemId as string).unwrap();
      setDeletingItemId(null);
      setShowDeleteModal(false);
    } catch (err) {
    }
  };

  const onSearch = (searchText: string) => {
    setPaginateQuery({ ...paginateQuery, search: searchText });
  };

  const getSortByColumnId = (columnId: number, sortDirection: string) => {
    let sortBy;
    switch (columnId) {
      case 1:
        sortBy = "name" + "." + i18n.language;
        break;
      case 5:
        sortBy = "for_gender";
        break;
      default:
        sortBy = "created_at";
        break;
    }
    return `${sortBy}:${sortDirection.toUpperCase()}`;
  };

  const onChangePage = (page: number) => {
    setPaginateQuery({ ...paginateQuery, page: page });
  };

  const onChangeRowsPerPage = (
    currentRowsPerPage: number,
    currentPage: number
  ) => {
    setPaginateQuery({
      ...paginateQuery,
      limit: currentRowsPerPage,
      page: currentPage,
    });
  };

  const onSort = (column: TableColumn<SchoolDto>, sortDirection: string) => {
    const sortBy = getSortByColumnId(column.id as number, sortDirection);
    setPaginateQuery({
      ...paginateQuery,
      sortBy: sortBy as string,
    });
  };

  const onChangeGovernorateFilter = (value: string) => {
    setFilterGovernorateId(value);
    setFilterCityId("");
    setPaginateQuery({
      ...paginateQuery,
      filter: {
        ["governorate_id"]: value,
      },
    });
  };

  const onChangeCityFilter = (value: string) => {
    setFilterCityId(value);
    setPaginateQuery({
      ...paginateQuery,
      filter: {
        ...paginateQuery.filter,
        ["city_id"]: value,
      },
    });
  };

  //#endregion

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={t("schools")} pageTitle={t("areas")} />
          <Row>
            <Col xxl={12}>
              <Card>
                <CardBody>
                  <Row className="g-2 mb-3">
                    <Col sm={4} className="mb-2">
                      <div className="search-box">
                        <DebounceInput
                          className="form-control"
                          placeholder={`${t("searchPlaceholder")}...`}
                          minLength={2}
                          debounceTimeout={300}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>
                          ) => onSearch(event.target.value)}
                          value={paginateQuery.search}
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </Col>
                    <Col sm={4} className="mb-2">
                      <div className="search-box">
                        <GovernorateSelect
                          placeholder={t("filterByGovernorate")}
                          type="select"
                          id="governorateId"
                          name="governorateId"
                          className="form-select"
                          aria-label={t("filterByGovernorate")}
                          onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            onChangeGovernorateFilter(event.target.value)
                          }
                          value={filterGovernorateId}
                        ></GovernorateSelect>
                      </div>
                    </Col>
                    <Col sm={4} className="mb-2">
                      <div className="search-box">
                        <div className="search-box">
                          <CitySelect
                            placeholder={t("filterByCity")}
                            type="select"
                            id="governorateId"
                            name="governorateId"
                            className="form-select"
                            aria-label={t("filterByCity")}
                            onChange={(event: ChangeEvent<HTMLInputElement>) =>
                              onChangeCityFilter(event.target.value)
                            }
                            governorateId={filterGovernorateId}
                            value={filterCityId}
                          ></CitySelect>
                        </div>
                      </div>
                    </Col>
                    <Col className="col-sm-auto ms-auto">
                      <div className="list-grid-nav hstack gap-1">
                        <Button color="success" onClick={() => onAdd()}>
                          <i className="ri-add-fill me-1 align-bottom"></i>
                          {t("addItem", { item: "school" })}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col sm={12}>
                      <AppTable
                        theme={"dark"}
                        columns={columns}
                        data={response?.data || []}
                        progressPending={loading}
                        progressComponent={<TableSpinner />}
                        pagination
                        paginationServer
                        sortServer
                        onSort={onSort}
                        onChangeRowsPerPage={onChangeRowsPerPage}
                        onChangePage={onChangePage}
                        defaultSortFieldId={defaultSortFieldId}
                        paginationDefaultPage={defaultPage}
                        defaultSortAsc={defaultSortAsc}
                        persistTableHead
                        paginationTotalRows={response?.meta?.totalItems}
                        paginationComponentOptions={{
                          rowsPerPageText: t("rowsPerPage"),
                          rangeSeparatorText: t("of"),
                        }}
                        fixedHeader={true}
                        fixedHeaderScrollHeight="60vh"
                      />
                    </Col>
                  </Row>
                  <DeleteModal
                    loading={deleteSchoolLoading}
                    show={showDeleteModal}
                    onDeleteClick={() => onDeleteConfirm()}
                    onCloseClick={() => {
                      setShowDeleteModal(false);
                    }}
                    error={deleteSchoolError ? ((deleteSchoolError as any)?.data?.message || "An error occurred") : undefined}
                  />
                  <AddEditSchoolModal
                    id={editingItemId}
                    show={showAddEditModal}
                    onCloseClick={() => {
                      setShowAddEditModal(false);
                      setEditingItemId(null);
                      
                    }}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export const SchoolsPage = withRouter(SchoolsPageComponent);
